import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssignedProperty,
  CustomerUserRequest,
  CustomerUserResponse,
  ListCustomersResponse,
  UpdateUserRbacRoleRequest,
  UpdateUserRbacRoleResponse,
  UserPropertiesRequest,
} from '@metron/shared';
import { useApiClient } from './useApiClient.ts';
import { USERS_QUERY_KEY } from './useUsers.ts';
import { toErrorMessage } from '@metron/shared';

const CUSTOMERS_QUERY_KEY = ['customers'] as const;

export function useCustomers() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  // Every write in this hook touches a user (their customer grant, their
  // property assignments, or their RBAC group), so each one invalidates the
  // users query — the same key useUsers uses.
  const invalidateUsers = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY });
  }, [queryClient]);

  // Fetch customers query
  const {
    data: customers = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: CUSTOMERS_QUERY_KEY,
    queryFn: async () => {
      const data = await apiClient.get<ListCustomersResponse>(
        'customers',
      );
      return data.customers;
    },
  });

  // Get user customers function (not converted to mutation as it's just a fetch)
  // Wrapped in useCallback to prevent infinite re-renders when used in useEffect dependencies
  const getUserCustomers = useCallback(async (userId: string): Promise<CustomerUserResponse> => {
    return await apiClient.get<CustomerUserResponse>(
      `users/${encodeURIComponent(userId)}/customers`,
    );
  }, [apiClient]);

  // Replace a user's customer grant (and role). Invalidates the users query on
  // success; the public signature stays `(userId, data) => Promise<...>`.
  const updateUserCustomersMutation = useMutation({
    mutationFn: (
      { userId, data }: { userId: string; data: CustomerUserRequest },
    ): Promise<CustomerUserResponse> =>
      apiClient.put<CustomerUserResponse>(
        `users/${encodeURIComponent(userId)}/customers`,
        data,
      ),
    onSuccess: invalidateUsers,
  });
  const updateUserCustomers = useCallback(
    (userId: string, data: CustomerUserRequest): Promise<CustomerUserResponse> =>
      updateUserCustomersMutation.mutateAsync({ userId, data }),
    [updateUserCustomersMutation.mutateAsync],
  );

  // Get the list of properties for a customer (for property user assignment)
  const getCustomerProperties = useCallback(async (
    customerId: number,
  ): Promise<AssignedProperty[]> => {
    return await apiClient.get<AssignedProperty[]>(
      `customers/${customerId}/properties`,
    );
  }, [apiClient]);

  // Replace all property assignments for a user. Invalidates the users query on
  // success; the public signature stays `(userId, data) => Promise<...>`.
  const updateUserPropertiesMutation = useMutation({
    mutationFn: (
      { userId, data }: { userId: string; data: UserPropertiesRequest },
    ): Promise<CustomerUserResponse> =>
      apiClient.put<CustomerUserResponse>(
        `users/${encodeURIComponent(userId)}/properties`,
        data,
      ),
    onSuccess: invalidateUsers,
  });
  const updateUserProperties = useCallback(
    (userId: string, data: UserPropertiesRequest): Promise<CustomerUserResponse> =>
      updateUserPropertiesMutation.mutateAsync({ userId, data }),
    [updateUserPropertiesMutation.mutateAsync],
  );

  // Update a user's RBAC role globally (Cognito group is the source of truth).
  // Invalidates the users query on success; the public signature stays
  // `(userId, data) => Promise<...>`.
  const updateUserRbacRoleMutation = useMutation({
    mutationFn: (
      { userId, data }: { userId: string; data: UpdateUserRbacRoleRequest },
    ): Promise<UpdateUserRbacRoleResponse> =>
      apiClient.patch<UpdateUserRbacRoleResponse>(
        `users/${encodeURIComponent(userId)}/rbac-role`,
        data,
      ),
    onSuccess: invalidateUsers,
  });
  const updateUserRbacRole = useCallback(
    (userId: string, data: UpdateUserRbacRoleRequest): Promise<UpdateUserRbacRoleResponse> =>
      updateUserRbacRoleMutation.mutateAsync({ userId, data }),
    [updateUserRbacRoleMutation.mutateAsync],
  );

  // Error handling - convert query error to string for backward compatibility
  const error = queryError ? (toErrorMessage(queryError, 'An error occurred')) : null;

  return {
    customers,
    isLoading,
    error,
    getUserCustomers,
    updateUserCustomers,
    getCustomerProperties,
    updateUserProperties,
    updateUserRbacRole,
    /** Escape hatch for composite saves — see lib/user-writes.ts. */
    invalidateUsers,
  };
}
